import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import Layout from "../../../../components/org/Layout";
import { Container, Grid, Link, Tab, Tabs, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "../../../../utils/i18n/lang";
import { messages } from "../../../../utils/i18n/i18n";
import { resultsRoute, seriesDetailRoute } from "../../../../routes/org";
import { pipe } from "fp-ts/function";
import {
    array,
    date,
    either,
    eq,
    nonEmptyArray,
    number,
    option,
    ord,
    record,
    task,
    taskEither,
} from "fp-ts";
import { QueryError } from "../../../../utils/supabase/QueryState";
import {
    errorsToMessage,
    recordOrdFromNumericMap,
    runTask,
} from "../../../../utils/fp";
import { SeriesRankingIndividuals } from "../../../../components/org/series/SeriesRankingIndividuals";
import {
    seriesDetailQuery,
    SeriesDetailRace,
    useSeriesResults,
} from "../../../../hooks/series";
import { shallowPush } from "../../../../utils/routing/routing";
import { RankingMode, rankingModeLabels } from "../../../../domain/series";
import { useRouter } from "next/router";
import { assertUnreachable } from "../../../../utils/assertUnreachable";
import { SeriesRankingClubs } from "../../../../components/org/series/SeriesRankingClubs";
import { contramap } from "fp-ts/Eq";
import { InternalLink } from "../../../../components/common/Link";
import {
    validIsoDateStringCodec,
    validIsoDateStringFromDateCodec,
} from "../../../../domain/common";
import CvkLayout from "../../../../components/customOrgs/cvk/CvkLayout";

type SsrError =
    | { type: "query"; reason: string }
    | {
          type: "fetch";
          reason: QueryError;
      };

export const getServerSideProps = async (
    context: GetServerSidePropsContext
) => {
    return pipe(
        { ...context.params, subdomain: "cvk" },
        seriesDetailRoute.queryCodec.decode,
        either.mapLeft(
            (e): SsrError => ({ type: "query", reason: errorsToMessage(e) })
        ),
        taskEither.fromEither,
        taskEither.chain((query) =>
            pipe(
                seriesDetailQuery(query.seriesId),
                taskEither.mapLeft(
                    (e): SsrError => ({
                        type: "fetch",
                        reason: e,
                    })
                ),
                taskEither.map((seriesDetail) => ({
                    seriesDetail,
                    query,
                }))
            )
        ),
        task.map(
            either.foldW(
                (e) => {
                    console.error(e);
                    // TODO better error handling?
                    return { notFound: true as const };
                },
                (props) => ({
                    props,
                })
            )
        ),
        runTask
    );
};
export default (p: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const m = useTranslation(msg);
    const modesM = useTranslation(rankingModeLabels);
    const router = useRouter();

    const [selectedMode, setSelectedMode] = React.useState(
        pipe(
            p.query.mode,
            option.fromNullable,
            option.getOrElse((): RankingMode => "individuals")
        )
    );

    const resultsQuery = useSeriesResults(
        pipe(
            p.seriesDetail.races,
            array.map((race) => race.id)
        )
    );

    return (
        <CvkLayout
            subdomain={p.query.subdomain}
            seo={{ title: `${p.seriesDetail.title}`, noindex: true }}
        >
            <Container maxWidth={"xl"}>
                {/*TODO move to common page heading and publish for mdx*/}
                <Typography variant={"h4"} component={"h1"} gutterBottom>
                    {p.seriesDetail.title}
                </Typography>
                {pipe(
                    p.seriesDetail.races,
                    array.uniq(seriesDetailRaceByEventIdEq),
                    array.sort(seriesDetailRaceByStartTimeOrd),
                    (races) =>
                        pipe(
                            races,
                            array.mapWithIndex((i, race) => (
                                <Grid item key={race.event_id}>
                                    <InternalLink
                                        href={resultsRoute.route({
                                            subdomain: p.query.subdomain,
                                            eventId: race.event_id,
                                        })}
                                    >
                                        <Link>{race.event.title}</Link>
                                    </InternalLink>
                                    {i + 1 < races.length ? " | " : null}
                                </Grid>
                            ))
                        ),
                    (items) => (
                        <Grid container spacing={1} sx={{ mb: 4 }}>
                            {items}
                        </Grid>
                    )
                )}

                <Typography variant={"h6"} component={"h2"}>
                    {m.rankingTitle}
                </Typography>

                <Tabs
                    sx={{ mt: 2, mb: 1 }}
                    value={selectedMode}
                    onChange={(e, newValue: RankingMode) => {
                        setSelectedMode(newValue);
                        shallowPush(
                            seriesDetailRoute.route({
                                ...p.query,
                                mode: newValue,
                            }),
                            router
                        );
                    }}
                >
                    {pipe(
                        modesM,
                        record.collect(rankingModeTabOrd)(
                            (rankingMode, label) => (
                                <Tab
                                    key={rankingMode}
                                    value={rankingMode}
                                    label={label}
                                />
                            )
                        )
                    )}
                </Tabs>

                {pipe(
                    p.seriesDetail.races,
                    nonEmptyArray.fromArray,
                    option.fold(
                        () => <Typography>{m.seriesWithoutRace}</Typography>,
                        (races) => {
                            const containerSx = { height: "65vh" };
                            switch (selectedMode) {
                                case "individuals":
                                    return (
                                        <SeriesRankingIndividuals
                                            subdomain={p.query.subdomain}
                                            resultsQuery={resultsQuery}
                                            containerSx={containerSx}
                                            raceList={races}
                                            topResultsCount={
                                                p.seriesDetail.top_results_count
                                            }
                                            ignoreCategories={
                                                p.seriesDetail.ignore_categories
                                            }
                                        />
                                    );

                                case "clubs":
                                    return (
                                        <SeriesRankingClubs
                                            subdomain={p.query.subdomain}
                                            containerSx={containerSx}
                                            resultsQuery={resultsQuery}
                                            raceList={races}
                                            topResultsCount={
                                                p.seriesDetail.top_results_count
                                            }
                                        />
                                    );
                            }

                            assertUnreachable(selectedMode);
                        }
                    )
                )}
            </Container>
        </CvkLayout>
    );
};

const msg = messages(
    {
        seriesWithoutRace: "No results were assigned to this series.",
        raceSectionTitle: "Results",
        rankingTitle: "Ranking",
    },
    {
        cs: {
            seriesWithoutRace: "V seriálu zatím nejsou vedeny žádné výsledky.",
            raceSectionTitle: "Závody seriálu",
            rankingTitle: "Pořadí",
        },
    }
);

const rankingModeTabOrd = recordOrdFromNumericMap<RankingMode>({
    individuals: 1,
    clubs: 2,
});

const seriesDetailRaceByEventIdEq = pipe(
    number.Eq,
    eq.contramap((race: SeriesDetailRace) => race.event_id)
);

const seriesDetailRaceByStartTimeOrd = pipe(
    date.Ord,
    ord.contramap((race: SeriesDetailRace) =>
        validIsoDateStringFromDateCodec.encode(race.start_time)
    )
);
